import React, { useEffect, useState } from 'react';
import { useTheme } from '../../context/ThemeContext';
import { formatCurrency } from '../../config/currency';
import api from '../../api/api';
import {
  BanknotesIcon,
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon,
  ShoppingCartIcon,
  UserGroupIcon,
  ChartBarIcon,
  CreditCardIcon,
  ClockIcon,
} from '@heroicons/react/24/outline';
import { LoadingSpinner } from '../LoadingSpinner';

interface SalesDashboardProps {
  vendorId: number;
  startDate: string;
  endDate: string;
}

interface HourlySales {
  hour: number;
  revenue: number;
  orders: number;
}

interface PaymentBreakdownFromAPI {
  cash: number;
  card: number;
  upi: number;
  other: number;
}

interface PaymentBreakdown {
  method: string;
  amount: number;
  count: number;
  percentage: number;
}

interface SalesDashboardDataFromAPI {
  totalRevenue: number;
  totalOrders: number;
  averageOrderValue: number;
  invoicedOrders?: number;
  averageTicketSize?: number;
  totalCustomers: number;
  revenueGrowth: number;
  orderGrowth: number;
  previousPeriodRevenue: number;
  previousPeriodOrders: number;
  hourlySales: HourlySales[];
  paymentBreakdown: PaymentBreakdownFromAPI;
}

interface SalesDashboardData {
  totalRevenue: number;
  totalOrders: number;
  averageOrderValue: number;
  invoicedOrders?: number;
  averageTicketSize?: number;
  totalCustomers: number;
  revenueGrowth: number;
  orderGrowth: number;
  previousPeriodRevenue: number;
  previousPeriodOrders: number;
  hourlySales: HourlySales[];
  paymentBreakdown: PaymentBreakdown[];
}

const SalesDashboard: React.FC<SalesDashboardProps> = ({ vendorId, startDate, endDate }) => {
  const { theme } = useTheme();
  const [data, setData] = useState<SalesDashboardData | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchSalesDashboard = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await api.get<SalesDashboardDataFromAPI>(
          `/api/vendors/${vendorId}/reports/sales-dashboard`,
          {
            params: { startDate, endDate },
          }
        );

        const apiData = response.data;

        // Transform payment breakdown from object to array format
        const paymentBreakdownArray: PaymentBreakdown[] = [];
        const pb = apiData.paymentBreakdown || { cash: 0, card: 0, upi: 0, other: 0 };
        const totalPayments = (pb.cash || 0) + (pb.card || 0) + (pb.upi || 0) + (pb.other || 0);

        if (pb.cash > 0) {
          paymentBreakdownArray.push({
            method: 'Cash',
            amount: pb.cash,
            count: 0, // Count not available from backend
            percentage: totalPayments > 0 ? (pb.cash / totalPayments) * 100 : 0
          });
        }
        if (pb.card > 0) {
          paymentBreakdownArray.push({
            method: 'Card',
            amount: pb.card,
            count: 0,
            percentage: totalPayments > 0 ? (pb.card / totalPayments) * 100 : 0
          });
        }
        if (pb.upi > 0) {
          paymentBreakdownArray.push({
            method: 'UPI',
            amount: pb.upi,
            count: 0,
            percentage: totalPayments > 0 ? (pb.upi / totalPayments) * 100 : 0
          });
        }
        if (pb.other > 0 || totalPayments === 0) {
          // Show "Other" if there's other payment or if total revenue exists but no payment method breakdown
          const otherAmount = pb.other > 0 ? pb.other : (apiData.totalRevenue || 0);
          if (otherAmount > 0) {
            paymentBreakdownArray.push({
              method: totalPayments === 0 ? 'Not Specified' : 'Other',
              amount: otherAmount,
              count: 0,
              percentage: totalPayments > 0 ? (pb.other / totalPayments) * 100 : 100
            });
          }
        }

        setData({
          ...apiData,
          paymentBreakdown: paymentBreakdownArray
        });
      } catch (err: unknown) {
        console.error('Failed to fetch sales dashboard:', err);
        setError('Failed to load sales data. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    fetchSalesDashboard();
  }, [vendorId, startDate, endDate]);

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-[400px]">
        <LoadingSpinner />
      </div>
    );
  }

  if (error) {
    return (
      <div
        className="glassmorphism-card p-8 text-center"
        style={{
          background: theme.components.card.background,
          border: `1px solid ${theme.components.card.border}`,
          borderRadius: '12px',
        }}
      >
        <div className="text-red-500 mb-2">
          <svg
            className="w-12 h-12 mx-auto"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
        </div>
        <p style={{ color: theme.text }}>{error}</p>
      </div>
    );
  }

  if (!data) {
    return null;
  }

  const maxHourlySales = Math.max(...data.hourlySales.map((h) => h.revenue), 1);

  return (
    <div className="space-y-6">
      {/* Header Cards - Key Metrics */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
        {/* Total Revenue - Large Card */}
        <div
          className="glassmorphism-card p-6 sm:col-span-2 lg:col-span-3 xl:col-span-2"
          style={{
            background: theme.components.card.background,
            border: `1px solid ${theme.components.card.border}`,
            borderRadius: '12px',
          }}
        >
          <div className="flex items-start justify-between">
            <div>
              <div className="flex items-center gap-2 mb-2">
                <BanknotesIcon className="w-6 h-6" style={{ color: theme.primary }} />
                <p className="text-sm font-medium" style={{ color: theme.gray[600] }}>
                  Total Revenue
                </p>
              </div>
              <h2 className="text-3xl lg:text-4xl font-bold mb-1" style={{ color: theme.text }}>
                {formatCurrency(data.totalRevenue)}
              </h2>
              <div className="flex items-center gap-2 mt-2">
                {data.revenueGrowth >= 0 ? (
                  <ArrowTrendingUpIcon className="w-5 h-5 text-green-500" />
                ) : (
                  <ArrowTrendingDownIcon className="w-5 h-5 text-red-500" />
                )}
                <span
                  className="text-sm font-semibold"
                  style={{ color: data.revenueGrowth >= 0 ? '#10B981' : '#EF4444' }}
                >
                  {data.revenueGrowth >= 0 ? '+' : ''}
                  {(data.revenueGrowth ?? 0).toFixed(1)}%
                </span>
                <span className="text-xs" style={{ color: theme.gray[500] }}>
                  vs previous period
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Total Orders */}
        <div
          className="glassmorphism-card p-6"
          style={{
            background: theme.components.card.background,
            border: `1px solid ${theme.components.card.border}`,
            borderRadius: '12px',
          }}
        >
          <div className="flex items-center gap-2 mb-2">
            <ShoppingCartIcon className="w-5 h-5" style={{ color: theme.success }} />
            <p className="text-sm font-medium" style={{ color: theme.gray[600] }}>
              Total Orders
            </p>
          </div>
          <h3 className="text-2xl font-bold" style={{ color: theme.text }}>
            {data.totalOrders.toLocaleString()}
          </h3>
          <div className="flex items-center gap-1 mt-1">
            {data.orderGrowth >= 0 ? (
              <ArrowTrendingUpIcon className="w-4 h-4 text-green-500" />
            ) : (
              <ArrowTrendingDownIcon className="w-4 h-4 text-red-500" />
            )}
            <span
              className="text-xs font-semibold"
              style={{ color: data.orderGrowth >= 0 ? '#10B981' : '#EF4444' }}
            >
              {data.orderGrowth >= 0 ? '+' : ''}
              {(data.orderGrowth ?? 0).toFixed(1)}%
            </span>
          </div>
        </div>

        {/* Average Ticket Size (revenue per invoiced order — accurate)
            Falls back to legacy averageOrderValue (revenue / all orders) if
            invoicedOrders not yet exposed by the API. */}
        <div
          className="glassmorphism-card p-6"
          style={{
            background: theme.components.card.background,
            border: `1px solid ${theme.components.card.border}`,
            borderRadius: '12px',
          }}
        >
          <div className="flex items-center gap-2 mb-2">
            <ChartBarIcon className="w-5 h-5" style={{ color: theme.accent }} />
            <p className="text-sm font-medium" style={{ color: theme.gray[600] }}>
              Avg Ticket Size
            </p>
          </div>
          <h3 className="text-2xl font-bold" style={{ color: theme.text }}>
            {formatCurrency(
              (data.averageTicketSize != null && data.invoicedOrders && data.invoicedOrders > 0)
                ? data.averageTicketSize
                : data.averageOrderValue
            )}
          </h3>
          <p className="text-xs mt-1" style={{ color: theme.gray[500] }}>
            {(data.invoicedOrders != null)
              ? `Per invoiced order (${data.invoicedOrders} of ${data.totalOrders})`
              : 'Per transaction'}
          </p>
        </div>

        {/* Total Customers */}
        <div
          className="glassmorphism-card p-6"
          style={{
            background: theme.components.card.background,
            border: `1px solid ${theme.components.card.border}`,
            borderRadius: '12px',
          }}
        >
          <div className="flex items-center gap-2 mb-2">
            <UserGroupIcon className="w-5 h-5" style={{ color: theme.warning }} />
            <p className="text-sm font-medium" style={{ color: theme.gray[600] }}>
              Total Customers
            </p>
          </div>
          <h3 className="text-2xl font-bold" style={{ color: theme.text }}>
            {data.totalCustomers.toLocaleString()}
          </h3>
          <p className="text-xs mt-1" style={{ color: theme.gray[500] }}>
            Unique customers
          </p>
        </div>
      </div>

      {/* Hourly Sales Chart */}
      <div
        className="glassmorphism-card p-6"
        style={{
          background: theme.components.card.background,
          border: `1px solid ${theme.components.card.border}`,
          borderRadius: '12px',
        }}
      >
        <div className="flex items-center gap-2 mb-4">
          <ClockIcon className="w-5 h-5" style={{ color: theme.primary }} />
          <h3 className="text-lg font-semibold" style={{ color: theme.text }}>
            Hourly Sales Distribution
          </h3>
        </div>
        <div className="space-y-3 overflow-x-auto">
          {data.hourlySales.length > 0 ? (
            <div className="min-w-[600px]">
              {data.hourlySales.map((hourData, index) => {
                const heightPercentage = (hourData.revenue / maxHourlySales) * 100;
                const displayHour = hourData.hour === 0 ? '12 AM' :
                  hourData.hour < 12 ? `${hourData.hour} AM` :
                  hourData.hour === 12 ? '12 PM' :
                  `${hourData.hour - 12} PM`;

                return (
                  <div key={index} className="flex items-center gap-3">
                    <div
                      className="w-20 text-xs font-medium flex-shrink-0"
                      style={{ color: theme.gray[600] }}
                    >
                      {displayHour}
                    </div>
                    <div className="flex-1 relative">
                      <div
                        className="h-8 rounded transition-all duration-300 hover:opacity-80"
                        style={{
                          width: `${heightPercentage}%`,
                          background: `linear-gradient(90deg, ${theme.primary}, ${theme.accent})`,
                          minWidth: hourData.revenue > 0 ? '2px' : '0',
                        }}
                      />
                    </div>
                    <div className="w-32 text-right flex-shrink-0">
                      <div className="text-sm font-semibold" style={{ color: theme.text }}>
                        {formatCurrency(hourData.revenue)}
                      </div>
                      <div className="text-xs" style={{ color: theme.gray[500] }}>
                        {hourData.orders} {hourData.orders === 1 ? 'order' : 'orders'}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <p className="text-center py-8" style={{ color: theme.gray[500] }}>
              No hourly sales data available for this period
            </p>
          )}
        </div>
      </div>

      {/* Payment Breakdown */}
      <div
        className="glassmorphism-card p-6"
        style={{
          background: theme.components.card.background,
          border: `1px solid ${theme.components.card.border}`,
          borderRadius: '12px',
        }}
      >
        <div className="flex items-center gap-2 mb-4">
          <CreditCardIcon className="w-5 h-5" style={{ color: theme.primary }} />
          <h3 className="text-lg font-semibold" style={{ color: theme.text }}>
            Payment Methods
          </h3>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {data.paymentBreakdown.length > 0 ? (
            data.paymentBreakdown.map((payment, index) => (
              <div key={index} className="p-4 rounded-lg" style={{ background: theme.gray[50] }}>
                <div className="flex justify-between items-center mb-2">
                  <span className="text-sm font-medium" style={{ color: theme.text }}>
                    {payment.method}
                  </span>
                  <span className="text-xs font-medium" style={{ color: theme.gray[600] }}>
                    {(payment.percentage ?? 0).toFixed(1)}%
                  </span>
                </div>
                <span className="text-lg font-semibold" style={{ color: theme.text }}>
                  {formatCurrency(payment.amount)}
                </span>
                <div className="mt-2">
                  <div
                    className="h-2 rounded-full"
                    style={{ background: theme.gray[200] }}
                  >
                    <div
                      className="h-2 rounded-full transition-all duration-300"
                      style={{
                        width: `${payment.percentage}%`,
                        background: theme.primary,
                      }}
                    />
                  </div>
                </div>
              </div>
            ))
          ) : (
            <p className="text-center py-4 col-span-full" style={{ color: theme.gray[500] }}>
              No payment data available
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default SalesDashboard;
